import React from "react";
import { Form as AntForm } from "antd";

// Legacy Form.create polyfill for AntD 6+
if (typeof AntForm.create !== "function") {
  const isEmptyValue = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const runCustomValidator = (rule, value, values) => {
    if (typeof rule.validator !== "function") {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      let callbackFinished = false;

      const handleValidatorResult = (resultValue) => {
        if (resultValue === undefined || resultValue === null || resultValue === true) {
          resolve();
          return;
        }

        if (resultValue === false) {
          reject(rule.message || "Campo invalido");
          return;
        }

        reject(resultValue instanceof Error ? resultValue.message : resultValue);
      };

      const callback = (error) => {
        callbackFinished = true;
        if (error === undefined || error === null || error === true) {
          resolve();
          return;
        }

        if (error === false) {
          reject(rule.message || "Campo invalido");
          return;
        }

        reject(error instanceof Error ? error.message : error);
      };

      try {
        const result = rule.validator(rule, value, callback, values);

        if (result && typeof result.then === "function") {
          result.then(handleValidatorResult).catch((err) => {
            reject(err instanceof Error ? err.message : err);
          });
          return;
        }

        // Sync validators may return true/false/string/Error directly.
        if (result !== undefined) {
          handleValidatorResult(result);
          return;
        }

        // Sync validators that don't use callback/promise are treated as success.
        if (rule.validator.length < 3 && !callbackFinished) {
          resolve();
        }
      } catch (err) {
        reject(err instanceof Error ? err.message : err);
      }
    });
  };

  const normalizeError = (message, fallback = "Campo invalido") => {
    if (!message) {
      return fallback;
    }
    return String(message);
  };

  const validateFieldRules = async (name, value, rules = [], values = {}) => {
    for (const rule of rules) {
      if (!rule) {
        continue;
      }

      if (rule.required && isEmptyValue(value)) {
        throw normalizeError(rule.message, "Campo obrigatorio");
      }

      if (rule.type === "email" && !isEmptyValue(value) && !emailRegex.test(String(value))) {
        throw normalizeError(rule.message, "E-mail invalido");
      }

      if (rule.pattern && !isEmptyValue(value) && !rule.pattern.test(String(value))) {
        throw normalizeError(rule.message, "Formato invalido");
      }

      if (typeof rule.validator === "function") {
        const customError = await runCustomValidator(rule, value, values).catch((err) => err);
        const isCustomValidationSuccess =
          customError === undefined || customError === null || customError === true;

        if (!isCustomValidationSuccess) {
          const normalizedCustomError =
            customError === false
              ? (rule.message || `Campo ${name} invalido`)
              : customError;

          throw normalizeError(normalizedCustomError, rule.message || `Campo ${name} invalido`);
        }
      }
    }
  };

  // Convert flat bracket-notation keys (e.g. "foo[bar][0]") into nested
  // objects/arrays, replicating the original antd v3 Form store behavior.
  const expandBracketKeys = (flat) => {
    const result = {};
    for (const [rawKey, value] of Object.entries(flat)) {
      if (!rawKey.includes("[")) {
        result[rawKey] = value;
        continue;
      }
      const segments = [];
      let buf = "";
      for (let i = 0; i < rawKey.length; i++) {
        const ch = rawKey[i];
        if (ch === "[") {
          if (buf) segments.push(buf);
          buf = "";
        } else if (ch === "]") {
          segments.push(buf);
          buf = "";
        } else {
          buf += ch;
        }
      }
      if (buf) segments.push(buf);
      let current = result;
      for (let i = 0; i < segments.length - 1; i++) {
        const seg = segments[i];
        const nextSeg = segments[i + 1];
        const nextIsIndex = /^\d+$/.test(nextSeg);
        if (
          current[seg] === undefined ||
          current[seg] === null ||
          typeof current[seg] !== "object"
        ) {
          current[seg] = nextIsIndex ? [] : {};
        }
        current = current[seg];
      }
      current[segments[segments.length - 1]] = value;
    }
    return result;
  };

  AntForm.create = function create(formOptions = {}) {
    return function withLegacyForm(WrappedComponent) {
      const Wrapper = (props) => {
        const fieldOptionsRef = React.useRef({});
        const touchedFieldsRef = React.useRef({});
        const fieldValuesRef = React.useRef({});
        const [fieldValues, setFieldValues] = React.useState({});

        const getAllValues = React.useCallback(() => {
          const values = { ...fieldValuesRef.current };
          Object.keys(fieldOptionsRef.current).forEach((fieldName) => {
            if (!Object.prototype.hasOwnProperty.call(values, fieldName)) {
              values[fieldName] = fieldOptionsRef.current[fieldName].initialValue;
            }
          });
          return values;
        }, []);

        const makeDecorator = (name, options = {}) => {
          const previous = fieldOptionsRef.current[name] || {};
          const merged = { ...previous, ...options };
          fieldOptionsRef.current[name] = merged;

          return (node) => {
          const hasField = Object.prototype.hasOwnProperty.call(fieldValues, name);
          const initialValue = merged.initialValue;

          if (!React.isValidElement(node)) {
            return node;
          }

          const valuePropName = merged.valuePropName || "value";
          const trigger = merged.trigger || "onChange";

          const decoratedProps = {
            [valuePropName]: hasField ? fieldValues[name] : initialValue,
            [trigger]: (...args) => {
              const eventValue = merged.getValueFromEvent
                ? merged.getValueFromEvent(...args)
                : args[0] && args[0].target
                  ? args[0].target[valuePropName]
                  : args[0];

              touchedFieldsRef.current[name] = true;
              fieldValuesRef.current[name] = eventValue;
              setFieldValues((prev) => {
                const nextValues = { ...prev, [name]: eventValue };
                if (typeof formOptions.onValuesChange === "function") {
                  formOptions.onValuesChange(props, { [name]: eventValue }, {
                    ...getAllValues(),
                    [name]: eventValue,
                  });
                }
                return nextValues;
              });

              const originalHandler = node.props && node.props[trigger];
              if (typeof originalHandler === "function") {
                originalHandler(...args);
              }
            },
          };

          return React.cloneElement(node, decoratedProps);
          };
        };

        const pickValues = React.useCallback(
          (names) => {
            const allValues = getAllValues();
            if (!names || names === true) {
              return allValues;
            }
            if (Array.isArray(names)) {
              return names.reduce((acc, name) => {
                acc[name] = allValues[name];
                return acc;
              }, {});
            }
            return { [names]: allValues[names] };
          },
          [getAllValues]
        );

        const toValidationError = (errorsByField) => ({
          errors: Object.entries(errorsByField).map(([field, message]) => ({ field, message })),
          fields: Object.entries(errorsByField).reduce((acc, [field, message]) => {
            acc[field] = [{ field, message }];
            return acc;
          }, {}),
        });

        const validateFieldNames = React.useCallback(async (names) => {
          const targetNames =
            names && names.length
              ? names
              : Object.keys(fieldOptionsRef.current);

          const values = pickValues(true);
          const errorsByField = {};

          for (const fieldName of targetNames) {
            const options = fieldOptionsRef.current[fieldName] || {};
            const rules = options.rules || [];
            const value = values[fieldName];

            try {
              await validateFieldRules(fieldName, value, rules, values);
            } catch (errorMessage) {
              errorsByField[fieldName] = normalizeError(errorMessage, `Campo ${fieldName} invalido`);
            }
          }

          if (Object.keys(errorsByField).length > 0) {
            throw toValidationError(errorsByField);
          }

          return pickValues(targetNames);
        }, [pickValues]);

        const validate = (names, cb) => {
          const promise = validateFieldNames(names);
          if (cb) {
            promise
              .then((values) => cb(null, expandBracketKeys(values)))
              .catch((err) => cb(err, expandBracketKeys(pickValues(names))));
          }
          return promise;
        };

        const validateFieldsAndScroll = (maybeCfg, cb) => {
          const names = Array.isArray(maybeCfg) ? maybeCfg : undefined;
          const realCb = typeof maybeCfg === "function" ? maybeCfg : cb;
          return validateFieldNames(names)
            .then((values) => realCb && realCb(null, expandBracketKeys(values)))
            .catch((err) => realCb && realCb(err, expandBracketKeys(pickValues(names || true))));
        };

        const legacyFormApi = {
          getFieldDecorator: (name, options) => makeDecorator(name, options),
          validateFields: (namesOrCb, maybeCb) =>
            typeof namesOrCb === "function"
              ? validate(undefined, namesOrCb)
              : validate(namesOrCb, maybeCb),
          validateFieldsAndScroll,
          resetFields: (names) => {
            setFieldValues((prev) => {
              const targetNames = Array.isArray(names) && names.length ? names : Object.keys(fieldOptionsRef.current);
              const next = { ...prev };
              targetNames.forEach((fieldName) => {
                delete touchedFieldsRef.current[fieldName];
                delete fieldValuesRef.current[fieldName];
                delete next[fieldName];
              });
              return next;
            });
          },
          setFieldsValue: (values) => {
            if (!values || typeof values !== "object") {
              return;
            }
            setFieldValues((prev) => {
              fieldValuesRef.current = { ...fieldValuesRef.current, ...values };
              const next = { ...prev, ...values };
              if (typeof formOptions.onValuesChange === "function") {
                formOptions.onValuesChange(props, values, {
                  ...getAllValues(),
                  ...values,
                });
              }
              return next;
            });
          },
          getFieldsValue: (names) => expandBracketKeys(pickValues(names)),
          getFieldValue: (name) => {
            const raw = getAllValues();
            if (Object.prototype.hasOwnProperty.call(raw, name)) return raw[name];
            return expandBracketKeys(raw)[name];
          },
        };

        return <WrappedComponent {...props} form={legacyFormApi} />;
      };

      Wrapper.displayName = `WithLegacyForm(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
      return Wrapper;
    };
  };
}

export default AntForm;
